import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import UiIcon from '../components/UiIcon';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog, fireCrmAlert } from '../utils/swalConfirm';
import '../styles/admin-medicos-visibilidad-premium.css';
import '../styles/grupo-bienestar-tyc-premium.css';

const ENDPOINTS = {
  list: '/api/asociacion-recetas/centro-regional',
  create: '/api/asociacion-recetas/centro-regional',
  update: (id) => `/api/asociacion-recetas/centro-regional/${id}`,
  delete: (id) => `/api/asociacion-recetas/centro-regional/${id}`
};

const EMPTY_DRAFT = {
  titulo: '',
  version: '',
  contenido: ''
};

function showInfoToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function buildDraftFromDoc(doc) {
  if (!doc) return { ...EMPTY_DRAFT };
  return {
    titulo: String(doc.titulo || '').trim(),
    version: String(doc.version || '').trim(),
    contenido: String(doc.contenido || '')
  };
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDateLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}

function CentroRegionalInformacion() {
  const [documento, setDocumento] = useState(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const loadDocumento = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const nextDoc = Array.isArray(json?.documentos) ? json.documentos[0] || null : null;
      setDocumento(nextDoc);
      setDraft(buildDraftFromDoc(nextDoc));
    } catch (e) {
      setError(e.message || 'No se pudo cargar la información.');
      setDocumento(null);
      setDraft({ ...EMPTY_DRAFT });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDocumento();
  }, [loadDocumento]);

  const hasDocumento = Boolean(documento?.id);
  const wordCount = useMemo(
    () => String(draft.contenido || '').trim().split(/\s+/).filter(Boolean).length,
    [draft.contenido]
  );
  const lineCount = useMemo(
    () => String(draft.contenido || '').split(/\r?\n/).length,
    [draft.contenido]
  );
  const charCount = useMemo(
    () => String(draft.contenido || '').length,
    [draft.contenido]
  );

  function updateField(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function openPreviewModal() {
    const titulo = String(draft.titulo || '').trim() || 'Ficha sin título';
    const version = String(draft.version || '').trim() || '—';
    const contenido = String(draft.contenido || '').trim() || 'Todavía no hay información cargada.';

    Swal.fire({
      title: 'Vista previa de la información',
      html: `
        <div class="crm-swal-body">
          <p class="crm-swal-lead">Revisá cómo quedará la ficha guardada en la base.</p>
          <dl class="crm-swal-details">
            <div class="crm-swal-detail"><dt>Título</dt><dd>${escapeHtml(titulo)}</dd></div>
            <div class="crm-swal-detail"><dt>Versión</dt><dd>${escapeHtml(version)}</dd></div>
          </dl>
          <div class="gbtyc-preview-popup">
            <pre class="gbtyc-preview-popup__body">${escapeHtml(contenido)}</pre>
          </div>
        </div>
      `,
      icon: false,
      confirmButtonText: 'Cerrar',
      customClass: {
        popup: 'swal-crm-popup',
        title: 'swal-crm-title',
        htmlContainer: 'swal-crm-html',
        confirmButton: 'swal-crm-confirm'
      },
      width: 'min(860px, 94vw)',
      padding: '1rem'
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const titulo = String(draft.titulo || '').trim();
    const version = String(draft.version || '').trim();
    const contenido = String(draft.contenido || '').trim();
    if (!titulo || !contenido) return;

    setSaving(true);
    try {
      const url = hasDocumento ? ENDPOINTS.update(documento.id) : ENDPOINTS.create;
      const res = await fetch(`${API_BASE_URL}${url}`, {
        method: hasDocumento ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo, version, contenido })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);

      const nextDoc = json?.documento || null;
      setDocumento(nextDoc);
      setDraft(buildDraftFromDoc(nextDoc));
      showInfoToast({
        title: hasDocumento ? 'Información actualizada' : 'Información agregada',
        text: hasDocumento
          ? 'Los cambios quedaron guardados.'
          : 'La ficha quedó creada en la tabla.'
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: hasDocumento ? 'No se pudo actualizar' : 'No se pudo crear',
        text: err.message || 'Error guardando la información.'
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!hasDocumento || deleting) return;
    const confirm = await fireConfirmDialog({
      title: 'Eliminar información',
      html: buildCrmSwalBody({
        lead: 'Se eliminará la ficha única de Centro Regional.',
        details: [
          { label: 'Título', value: documento.titulo || '—' },
          { label: 'Versión', value: documento.version || '—' }
        ],
        items: [
          'La ficha desaparecerá del panel',
          'Podés volver a cargarla después desde el mismo formulario'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      width: 'min(440px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(documento.id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      setDocumento(null);
      setDraft({ ...EMPTY_DRAFT });
      showInfoToast({
        title: 'Información eliminada',
        text: 'Ya quedó disponible para cargarla de nuevo.'
      });
    } catch (err) {
      fireCrmAlert({
        icon: 'error',
        title: 'No se pudo eliminar',
        text: err.message || 'Error borrando la información.'
      });
    } finally {
      setDeleting(false);
    }
  }

  function resetDraft() {
    setDraft(hasDocumento ? buildDraftFromDoc(documento) : { ...EMPTY_DRAFT });
  }

  if (loading) {
    return (
      <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true" style={{ height: '100vh', margin: '15px' }}>
        <div className="crm-gd-spinner" aria-hidden="true" />
        <div className="crm-gd-loading__title">Cargando información</div>
        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Buscando la ficha única de Centro Regional…</p>
      </div>
    );
  }

  return (
    <section className="crm-amv-page crm-gd-page crm-pbody gbtyc-page">
      {error ? <div className="crm-amv-alert" role="alert">{error}</div> : null}

      <section className="crm-amv-hero-panel gbtyc-hero" aria-labelledby="centro-regional-title">
        <div className="crm-amv-hero-panel__head gbtyc-hero__head">
          <div className="crm-amv-hero-panel__brand">
            <div className="crm-amv-hero-panel__icon" aria-hidden="true">🏥</div>
            <div className="crm-amv-hero-panel__titles">
              <p className="crm-amv-hero-panel__eyebrow">Información institucional única</p>
              <h2 id="centro-regional-title" className="crm-amv-hero-panel__title">
                Centro Regional
              </h2>
            </div>
          </div>
          <div className="gbtyc-hero__badges">
            <span className={`gbtyc-chip${hasDocumento ? ' is-active' : ' is-draft'}`}>{hasDocumento ? 'Registrado' : 'Borrador'}</span>
            <span className="gbtyc-chip">1 ficha</span>
            <span className="gbtyc-chip">Edición total</span>
          </div>
        </div>

        <div className="gbtyc-hero__stats">
          <div className="crm-amv-hero-stat">
            <span className="crm-amv-hero-stat__num">{hasDocumento ? 1 : 0}</span>
            <span className="crm-amv-hero-stat__label">Ficha</span>
          </div>
          <div className="crm-amv-hero-stat crm-amv-hero-stat--visible">
            <span className="crm-amv-hero-stat__num">{wordCount}</span>
            <span className="crm-amv-hero-stat__label">Palabras</span>
          </div>
          <div className="crm-amv-hero-stat crm-amv-hero-stat--hidden">
            <span className="crm-amv-hero-stat__num">{lineCount}</span>
            <span className="crm-amv-hero-stat__label">Líneas</span>
          </div>
          <div className="crm-amv-hero-stat">
            <span className="crm-amv-hero-stat__num">{charCount}</span>
            <span className="crm-amv-hero-stat__label">Caracteres</span>
          </div>
        </div>
      </section>

      <div className="gbtyc-grid">
        <article className="gbtyc-card gbtyc-card--summary">
          <div className="gbtyc-card__head">
            <div>
              <p className="gbtyc-card__eyebrow">Estado</p>
              <h3 className="gbtyc-card__title">{hasDocumento ? 'Ficha activa' : 'Sin información cargada'}</h3>
            </div>
            <div className="gbtyc-state-actions">
              <span className={`gbtyc-state-pill${hasDocumento ? ' is-on' : ' is-off'}`}>{hasDocumento ? 'Activa' : 'Vacía'}</span>
              <button
                type="button"
                className="gbtyc-btn gbtyc-btn--ghost"
                onClick={openPreviewModal}
                disabled={!String(draft.contenido || '').trim()}
              >
                <UiIcon name="eye" size={14} />
                Vista previa
              </button>
            </div>
          </div>

          {hasDocumento ? (
            <div className="gbtyc-summary">
              <div className="gbtyc-summary__row"><span>Id</span><strong>{documento.id}</strong></div>
              <div className="gbtyc-summary__row"><span>Título</span><strong>{documento.titulo || '—'}</strong></div>
              <div className="gbtyc-summary__row"><span>Versión</span><strong>{documento.version || '—'}</strong></div>
              <div className="gbtyc-summary__row"><span>Creado</span><strong>{formatDateLabel(documento.created_at)}</strong></div>
              <div className="gbtyc-summary__row"><span>Actualizado</span><strong>{formatDateLabel(documento.updated_at)}</strong></div>
            </div>
          ) : (
            <div className="gbtyc-empty">
              <div className="gbtyc-empty__icon" aria-hidden="true">🏥</div>
              <p className="gbtyc-empty__title">Todavía no hay información guardada</p>
              <p className="gbtyc-empty__text">
                Cargá la información institucional de Centro Regional, guardala y editála cuando haga falta.
              </p>
            </div>
          )}

          <div className="gbtyc-summary__actions">
            <button type="button" className="gbtyc-btn gbtyc-btn--ghost" onClick={resetDraft} disabled={saving || deleting}>
              <UiIcon name="refresh" size={14} />
              Restaurar
            </button>
            <button
              type="button"
              className="gbtyc-btn gbtyc-btn--danger"
              onClick={() => void handleDelete()}
              disabled={!hasDocumento || saving || deleting}
            >
              <UiIcon name="trash" size={14} />
              Eliminar
            </button>
          </div>
        </article>

        <article className="gbtyc-card gbtyc-card--editor">
          <div className="gbtyc-card__head">
            <div>
              <p className="gbtyc-card__eyebrow">{hasDocumento ? 'Edición' : 'Alta'}</p>
              <h3 className="gbtyc-card__title">{hasDocumento ? 'Modificar información' : 'Crear ficha única'}</h3>
            </div>
            <span className="gbtyc-subtle-note">{hasDocumento ? 'Contenido guardado' : 'Aún sin guardar'}</span>
          </div>

          <form className="gbtyc-form" onSubmit={handleSubmit}>
            <div className="gbtyc-form__row">
              <label className="gbtyc-field">
                <span className="gbtyc-field__label">Título</span>
                <input
                  className="gbtyc-input"
                  type="text"
                  value={draft.titulo}
                  onChange={(e) => updateField('titulo', e.target.value)}
                  placeholder="Centro Regional - Información"
                  autoComplete="off"
                />
              </label>
              <label className="gbtyc-field gbtyc-field--small">
                <span className="gbtyc-field__label">Versión</span>
                <input
                  className="gbtyc-input"
                  type="text"
                  value={draft.version}
                  onChange={(e) => updateField('version', e.target.value)}
                  placeholder="v1"
                  autoComplete="off"
                />
              </label>
            </div>

            <label className="gbtyc-field gbtyc-field--content">
              <span className="gbtyc-field__label">Información completa</span>
              <textarea
                className="gbtyc-textarea"
                value={draft.contenido}
                onChange={(e) => updateField('contenido', e.target.value)}
                placeholder="Pegá aquí la información completa de Centro Regional…"
                rows={20}
              />
            </label>

            <div className="gbtyc-form__meta">
              <span className="gbtyc-meta-chip">Guardado en una sola tabla</span>
              <span className="gbtyc-meta-chip">Se puede editar, borrar y volver a crear</span>
            </div>

            <div className="gbtyc-actions">
              <button type="button" className="gbtyc-btn gbtyc-btn--ghost" onClick={resetDraft} disabled={saving || deleting}>
                <UiIcon name="refresh" size={14} />
                Deshacer
              </button>
              <button
                type="submit"
                className="gbtyc-btn gbtyc-btn--primary"
                disabled={saving || deleting || !String(draft.titulo || '').trim() || !String(draft.contenido || '').trim()}
              >
                <UiIcon name={hasDocumento ? 'edit' : 'plus'} size={14} />
                {saving ? 'Guardando…' : hasDocumento ? 'Guardar cambios' : 'Agregar información'}
              </button>
            </div>
          </form>
        </article>
      </div>
    </section>
  );
}

export default CentroRegionalInformacion;
